import {
    ChangeDetectionStrategy,
    Component,
    ExistingProvider,
    forwardRef,
    Inject,
    Input,
    OnChanges,
    Optional
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash-es';
import {AsyncSubject, combineLatest, of, Subject} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {TypedSimpleChanges} from '../../../common/typed-on-changes';
import {AccountService} from '../../../components/accounts';
import {destroy$} from '../../../components/destroyable';
import {SelectItem} from '../../../components/select-input/select-item';
import {isSmartSecurityKey, SmartSecurityService} from '../../../components/smart-security';
import {AccountType} from '../../../enums/account-type';
import {Exchange, isExchangeAllowedToTrade, isExchangeWithSplitAccount} from '../../../enums/exchange';
import {
    DisplayExchangeAccount,
    DisplayExchangeAccountFactory
} from '../../../factories/display-exchange-account.factory';
import {ExchangeAccount, ExchangeAccountBase, isExchangeAccountBaseEqual} from '../../../models/exchange-account';
import {SecurityKey} from '../../../models/security-key';
import {IS_DETACHED_WIDGET} from '../../../pages/widget-page/detached-widget-injection';
import {UserService} from '../../../services/user.service';

const SELECT_ACCOUNT_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
    provide:     NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SelectAccountComponent),
    multi:       true
};

interface AccountSelectItem extends SelectItem {
    label: string;
    value: ExchangeAccountBase;
}

enum SelectAccountState {
    UNKNOWN,
    NOT_SUPPORTED,
    SMART,
    ACCOUNT_NEEDED,
    ENABLED
}

@Component({
    selector: 'select-account',
    templateUrl: './select-account.component.html',
    styleUrls: ['./select-account.component.scss'],
    providers: [SELECT_ACCOUNT_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectAccountComponent implements ControlValueAccessor, OnChanges {

    @Input()
    public securityKey: SecurityKey | undefined;

    public readonly destroy$ = destroy$();

    public displayAccount: DisplayExchangeAccount;
    public accountBase: ExchangeAccountBase;

    public accountBaseSelectItems: AccountSelectItem[];

    public isDisabled: boolean = false;

    public state: SelectAccountState = SelectAccountState.UNKNOWN;

    public readonly SelectAccountState = SelectAccountState;

    public accountCreationLinkExchange: Exchange = null;

    /**
     * Tells whether Accounts page is allowed to the user's role
     */
    public readonly canManageAccounts = AccountService.canUserRoleManageAccounts(this.userService.user.role);

    private readonly writeValue$ = new Subject<ExchangeAccountBase>();
    private readonly securityKey$ = new Subject<SecurityKey>();

    private propagateChange: (accountBase: ExchangeAccountBase) => void;
    private propagateOnTouched: () => void;

    private readonly initialized$ = new AsyncSubject<boolean>();

    constructor(
        @Optional()
        @Inject(IS_DETACHED_WIDGET)
        public isDetachedWidget: boolean,

        private accountService: AccountService,
        private translationService: L10nTranslationService,
        private smartSecurityService: SmartSecurityService,
        private userService: UserService
    ) {
        this.listenToChanges();
    }

    public writeValue(accountBase: ExchangeAccountBase): void {
        this.accountBase = accountBase;
        this.updateDisplayAccount();
        this.writeValue$.next(accountBase);
    }

    public registerOnChange(handler: (accountBase: ExchangeAccountBase) => void): void {
        this.propagateChange = handler;
    }

    public registerOnTouched(handler: () => void): void {
        this.propagateOnTouched = handler;

        /**
         * The method {@link registerOnTouched} is called last while initializing controlValueAccessor.
         */
        this.initialized$.next(true);
        this.initialized$.complete();
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    public ngOnChanges(changes: TypedSimpleChanges<SelectAccountComponent>): void {
        if (changes.securityKey) {
            this.securityKey$.next(this.securityKey);
        }
    }

    public onChangeAccountBase(accountBase: ExchangeAccountBase): void {
        if (!isExchangeAccountBaseEqual(this.accountBase, accountBase)) {
            this.accountBase = accountBase;
            this.updateDisplayAccount();
            this.propagateChange(accountBase);
        }
    }

    private getDisplayAccountName(account: ExchangeAccount, accountType?: AccountType): string {
        return DisplayExchangeAccountFactory.getDisplayAccountName(
            account,
            true,
            accountType,
            this.translationService
        );
    }

    private prepareSelectItems(accounts: ExchangeAccount[]): void {
        const selectItemReducer = (selectItems: AccountSelectItem[], account: ExchangeAccount) =>
            isExchangeWithSplitAccount(account.exchange)
                ? selectItems.concat([
                    {
                        value: {
                            id:   account.id,
                            type: AccountType.CASH
                        },
                        label: this.getDisplayAccountName(account, AccountType.CASH)
                    },
                    {
                        value: {
                            id:   account.id,
                            type: AccountType.MARGIN
                        },
                        label: this.getDisplayAccountName(account, AccountType.MARGIN)
                    }
                ])
                : selectItems.concat([
                    {
                        value: {
                            id:   account.id,
                            type: account.type
                        },
                        label: this.getDisplayAccountName(account)
                    }
                ]);

        this.accountBaseSelectItems = _.sortBy(
            accounts.reduce(selectItemReducer, []),
            (account: AccountSelectItem) => account.label.toLowerCase()
        );
    }

    private listenToChanges(): void {
        const security$ = this.securityKey$.pipe(
            switchMap(securityKey => securityKey
                ? this.smartSecurityService.getSecurityBySecurityKey(securityKey)
                : of(null)
            )
        );

        const accounts$ = this.securityKey$.pipe(
            switchMap(securityKey =>
                !securityKey
                    ? of(this.accountService.getAllAccountsFromCache())
                    : securityKey.securityExchange !== Exchange.SMART
                        ? this.smartSecurityService.getSecurityUserAccounts(securityKey)
                        : of([])
            ),
            tap(accounts => this.prepareSelectItems(accounts))
        );

        combineLatest([
            security$,
            accounts$,
            this.writeValue$,
            this.initialized$
        ]).pipe(
            tap(([security, accounts]) => {
                const accountBase = this.accountBase;

                this.state = security && !isExchangeAllowedToTrade(this.securityKey.securityExchange)
                    ? SelectAccountState.NOT_SUPPORTED
                    : security && isSmartSecurityKey(security)
                        ? SelectAccountState.SMART
                        : !accounts.length
                            ? SelectAccountState.ACCOUNT_NEEDED
                            : SelectAccountState.ENABLED;

                this.onChangeAccountBase(accountBase);
            }),
            switchMap(([securityKey]) =>
                (
                    this.state === SelectAccountState.ACCOUNT_NEEDED
                    && securityKey && isSmartSecurityKey(securityKey)
                )
                    ? this.smartSecurityService.getSmartSecurityAccountCreationExchange(securityKey)
                    : of(securityKey && securityKey.securityExchange)
            ),
            tap(accountCreationLinkExchange => this.accountCreationLinkExchange = accountCreationLinkExchange),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateDisplayAccount(): void {
        const account = this.accountService.getAccountByIdFromCache(this.accountBase && this.accountBase.id);
        this.displayAccount = DisplayExchangeAccountFactory.createDisplayAccountOrSmart(account, this.securityKey);
        this.displayAccount.name = this.getDisplayAccountName(account, this.accountBase && this.accountBase.type);
    }

}
